import React from 'react';
import { useRecoilValue } from 'recoil';
import { blockSettings } from 'venn-state';
import {
  assertExhaustive,
  type FixedIncomeHoldingsBlocksType,
  isHoldingsFixedIncomeBlock,
  Numbers,
  plural,
} from 'venn-utils';
import { Block } from '../../components/core';
import { useFixedIncomeSubjects } from '../../../studio-blocks/logic/useFixedIncomeSubjects';
import { isEmpty, last } from 'lodash';

export const HoldingsCoverageFooter = ({ id }: { id: string }) => {
  const settings = useRecoilValue(blockSettings(id));
  const { subjectsWithFixedIncome, subjectsWithoutFixedIncome } = useFixedIncomeSubjects(id);

  if (
    !isHoldingsFixedIncomeBlock(settings.customBlockType) ||
    (subjectsWithFixedIncome.length === 0 && subjectsWithoutFixedIncome.length === 0)
  ) {
    return null;
  }

  return (
    <Block.Footer id={id}>
      {getCoverageFooterText(settings.customBlockType, subjectsWithFixedIncome, subjectsWithoutFixedIncome)}
    </Block.Footer>
  );
};

const getCoverageFooterText = (
  blockType: FixedIncomeHoldingsBlocksType,
  subjectsWithFixedIncome: { name?: string; coverage: number }[],
  subjectsWithoutFixedIncome: { name?: string }[],
): string => {
  if (blockType === 'MUNI_BREAKDOWN') {
    return getMuniCoverageFooterText(subjectsWithFixedIncome, subjectsWithoutFixedIncome);
  }
  return getDefaultCoverageFooterText(blockType, subjectsWithFixedIncome, subjectsWithoutFixedIncome);
};

const getMuniCoverageFooterText = (
  subjectsWithData: { name?: string; coverage: number }[],
  subjectsWithoutData: { name?: string }[],
): string => {
  const baseText =
    'Muni - States and Territories breakdown is based on the long rescaled municipal positions with available states or territories data';

  const coverageText = isEmpty(subjectsWithData)
    ? ''
    : plural(subjectsWithData.length, {
        1: ` and reflects ${Numbers.safeFormatPercentage(subjectsWithData[0]?.coverage)} of ${
          subjectsWithData[0]?.name
        }'s total long allocations`,
        other: ` and reflects ${subjectsWithData
          .slice(0, -1)
          .map(({ name, coverage }) => `${Numbers.safeFormatPercentage(coverage)} of ${name}'s`)
          .join(', ')}, and ${Numbers.safeFormatPercentage(last(subjectsWithData)?.coverage)} of ${
          last(subjectsWithData)?.name
        }'s total long allocations`,
      });

  const noDataText = isEmpty(subjectsWithoutData)
    ? ''
    : plural(subjectsWithoutData.length, {
        1: ` ${subjectsWithoutData[0]?.name} contains no holdings with available municipal bond states and territories data.`,
        other: ` ${subjectsWithoutData
          .slice(0, -1)
          .map((s) => s?.name)
          .join(
            ', ',
          )}, and ${last(subjectsWithoutData)?.name} contain no holdings with available municipal bond states and territories data.`,
      });

  return `${baseText}${coverageText}.${noDataText}`;
};

const getDefaultCoverageFooterText = (
  blockType: FixedIncomeHoldingsBlocksType,
  subjectsWithFixedIncome: { name?: string; coverage: number }[],
  subjectsWithoutFixedIncome: { name?: string }[],
): string => {
  const dataName = getDataName(blockType);
  const baseText = `${dataName} data is based on the long rescaled positions of fixed income holdings`;

  const fixedIncomeText = isEmpty(subjectsWithFixedIncome)
    ? ''
    : plural(subjectsWithFixedIncome.length, {
        1: ` and reflects ${Numbers.safeFormatPercentage(subjectsWithFixedIncome[0]?.coverage)} of ${
          subjectsWithFixedIncome[0]?.name
        }'s total long allocations`,
        other: ` and reflects ${subjectsWithFixedIncome
          .slice(0, -1)
          .map(({ name, coverage }) => {
            const coveragePercent = Numbers.safeFormatPercentage(coverage);
            return `${coveragePercent} of ${name}'s`;
          })
          .join(', ')}, and ${Numbers.safeFormatPercentage(last(subjectsWithFixedIncome)?.coverage)} of ${
          last(subjectsWithFixedIncome)?.name
        }'s total long allocations`,
      });

  const noFixedIncomeText = isEmpty(subjectsWithoutFixedIncome)
    ? ''
    : plural(subjectsWithoutFixedIncome.length, {
        1: ` ${subjectsWithoutFixedIncome[0]?.name} contains no fixed income holdings.`,
        2: ` ${subjectsWithoutFixedIncome[0]?.name} and ${subjectsWithoutFixedIncome[1]?.name} contain no fixed income holdings.`,
        other: ` ${subjectsWithoutFixedIncome
          .slice(0, -1)
          .map((s) => s?.name)
          .join(', ')}, and ${last(subjectsWithoutFixedIncome)?.name} contain no fixed income holdings.`,
      });

  return `${baseText}${fixedIncomeText}.${noFixedIncomeText}`;
};

const getDataName = (blockType: FixedIncomeHoldingsBlocksType) => {
  switch (blockType) {
    case 'CREDIT_BREAKDOWN':
      return 'Credit quality';
    case 'MATURITY_BREAKDOWN':
      return 'Maturity';
    case 'DURATION_BREAKDOWN':
      return 'Duration';
    case 'MUNI_BREAKDOWN':
      return 'Municipal';
    default:
      throw assertExhaustive(blockType);
  }
};
